/**
 * PortalThemeContext
 *
 * Applies the tenant's portal theme (from `Company.portal_theme`) as CSS
 * custom properties on `document.documentElement` so Tailwind tokens like
 * `bg-tenant-primary`, `text-tenant-on`, `hover:bg-tenant-hover` pick up the
 * right color per tenant.
 *
 * Resolution order for each color:
 *   1. Explicit override in `company.portal_theme`
 *   2. `company.primary_color` / `secondary_color` (when portal_theme is empty)
 *   3. OWLY default (hardcoded fallback — #171717 black)
 *
 * Works for brokers & agencies switching between companies: when the
 * `effectiveCompany` changes (via TenantContext), the CSS vars are updated
 * and everything using `tenant-*` tokens re-paints instantly.
 */
import React, { createContext, useContext, useEffect, useMemo } from 'react'
import { useTenant } from './TenantContext'
import { useAuth } from './AuthContext'
import { useTheme } from './ThemeContext'

// ─────────────────────────────────────────────────────────────────────────
// Defaults (mirror apps/companies/palette.py)
// ─────────────────────────────────────────────────────────────────────────
const DEFAULT_PRIMARY = '#171717'
const DEFAULT_PRIMARY_HOVER = '#0a0a0a'
const DEFAULT_ACCENT = '#10b981'
const DEFAULT_ON_PRIMARY = '#ffffff'

// ─────────────────────────────────────────────────────────────────────────
// Color helpers
// ─────────────────────────────────────────────────────────────────────────
function hexToRgb(hex: string): [number, number, number] | null {
  const h = hex.replace('#', '')
  const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h
  if (full.length !== 6) return null
  const n = parseInt(full, 16)
  if (Number.isNaN(n)) return null
  return [(n >> 16) & 0xff, (n >> 8) & 0xff, n & 0xff]
}

function darken(hex: string, factor = 0.1): string {
  const rgb = hexToRgb(hex)
  if (!rgb) return DEFAULT_PRIMARY_HOVER
  const shift = (c: number) => Math.max(0, Math.min(255, Math.round(c * (1 - factor))))
  return '#' + rgb.map(shift).map((c) => c.toString(16).padStart(2, '0')).join('')
}

function relativeLuminance(hex: string): number {
  const rgb = hexToRgb(hex)
  if (!rgb) return 0
  const ch = (c: number) => {
    const n = c / 255
    return n <= 0.03928 ? n / 12.92 : ((n + 0.055) / 1.055) ** 2.4
  }
  return 0.2126 * ch(rgb[0]) + 0.7152 * ch(rgb[1]) + 0.0722 * ch(rgb[2])
}

function contrastText(bgHex: string): string {
  return relativeLuminance(bgHex) < 0.5 ? '#ffffff' : '#171717'
}

// "R, G, B" triplet for use inside `rgba(var(--x), opacity)` in CSS
function hexToRgbTriplet(hex: string | undefined | null): string {
  if (!hex) return '59, 130, 246'
  const rgb = hexToRgb(hex)
  return rgb ? rgb.join(', ') : '59, 130, 246'
}

// ─────────────────────────────────────────────────────────────────────────
// Resolution
// ─────────────────────────────────────────────────────────────────────────
export interface PortalTheme {
  primary: string
  primaryHover: string
  accent: string
  textOnPrimary: string
  surface: string       // rgba — card bg
  elevated: string      // rgba — input/subtle surface bg
  border: string
  cardSelected: string  // rgba — selected step card bg
  bgGrad1: string       // first color stop of the page gradient
  bgGrad2: string       // second color stop
  source: 'company' | 'project' | 'default'
}

const DEFAULT_SURFACE = 'rgba(255, 255, 255, 0.95)'
const DEFAULT_ELEVATED = 'rgba(255, 255, 255, 0.85)'
const DEFAULT_BORDER = '#e5e5e5'
const DEFAULT_CARD_SELECTED = 'rgba(23, 23, 23, 0.06)'
const DEFAULT_BG_GRAD_1 = '#e0e7ff'
const DEFAULT_BG_GRAD_2 = '#fae8ff'

function resolveTheme(company: any, activeTheme: 'light' | 'dark'): PortalTheme {
  if (!company) {
    return {
      primary: DEFAULT_PRIMARY,
      primaryHover: DEFAULT_PRIMARY_HOVER,
      accent: DEFAULT_ACCENT,
      textOnPrimary: DEFAULT_ON_PRIMARY,
      surface: DEFAULT_SURFACE,
      elevated: DEFAULT_ELEVATED,
      border: DEFAULT_BORDER,
      cardSelected: DEFAULT_CARD_SELECTED,
      bgGrad1: DEFAULT_BG_GRAD_1,
      bgGrad2: DEFAULT_BG_GRAD_2,
      source: 'default',
    }
  }

  const theme = (company.portal_theme || {}) as any
  // The active theme comes from useTheme() (already resolved from
  // user toggle / system preference). The `defaultMode` field on
  // portal_theme is only a server-side hint for the *initial* mode the
  // tenant prefers; once the user toggles the sun/moon button we follow
  // their choice — otherwise the dark-mode palette would never apply
  // when an admin configured colors for it.
  const useDark = activeTheme === 'dark'
  const modeConfig = (useDark ? theme.dark : theme.light) || null

  const primary = modeConfig?.primary || theme.primary || company.primary_color || DEFAULT_PRIMARY
  const primaryHover = modeConfig?.primaryHover || theme.primary_hover || darken(primary, 0.1)
  const accent = modeConfig?.accent || theme.accent || company.secondary_color || DEFAULT_ACCENT
  const textOnPrimary = modeConfig?.textOnPrimary || theme.text_on_primary || contrastText(primary)
  const surface = modeConfig?.surface || DEFAULT_SURFACE
  const elevated = modeConfig?.elevated || DEFAULT_ELEVATED
  const border = modeConfig?.border || DEFAULT_BORDER
  const cardSelected = modeConfig?.cardSelected || DEFAULT_CARD_SELECTED
  const bgGrad1 = modeConfig?.organicBackground?.color1 || DEFAULT_BG_GRAD_1
  const bgGrad2 = modeConfig?.organicBackground?.color2 || DEFAULT_BG_GRAD_2

  const hasExplicit = Boolean(modeConfig?.primary || theme.primary)
  return {
    primary,
    primaryHover,
    accent,
    textOnPrimary,
    surface,
    elevated,
    border,
    cardSelected,
    bgGrad1,
    bgGrad2,
    source: hasExplicit ? 'company' : 'default',
  }
}

// ─────────────────────────────────────────────────────────────────────────
// Context
// ─────────────────────────────────────────────────────────────────────────
const PortalThemeContext = createContext<PortalTheme | null>(null)

export function PortalThemeProvider({ children }: { children: React.ReactNode }) {
  const { companies, selectedCompanyFilter, selectedProject } = useTenant()
  const { user } = useAuth()
  const { actualTheme } = useTheme()

  // Mirrors the logic in Sidebar.tsx's effectiveCompanyData to stay in sync.
  const effectiveCompany = useMemo(() => {
    const role = user?.role
    const isRestrictedPlatform = role === 'owly_broker' || role === 'owly_agency'

    if (isRestrictedPlatform) {
      if (companies.length === 1) return companies[0] as any
      if (selectedCompanyFilter && selectedCompanyFilter !== 'all') {
        return (companies.find((c: any) => c.id === selectedCompanyFilter) as any)
          || (selectedProject as any)
          || null
      }
      return (companies[0] as any) || (selectedProject as any) || null
    }
    return (user as any)?.company_data || null
  }, [user, companies, selectedCompanyFilter, selectedProject])

  const theme = useMemo(
    () => resolveTheme(effectiveCompany, actualTheme),
    [effectiveCompany, actualTheme],
  )

  // Apply CSS vars on <html> element
  useEffect(() => {
    const root = document.documentElement
    root.style.setProperty('--color-tenant-primary', theme.primary)
    root.style.setProperty('--color-tenant-hover', theme.primaryHover)
    root.style.setProperty('--color-tenant-accent', theme.accent)
    root.style.setProperty('--color-tenant-on', theme.textOnPrimary)
    root.style.setProperty('--color-tenant-surface', theme.surface)
    root.style.setProperty('--color-tenant-elevated', theme.elevated)
    root.style.setProperty('--color-tenant-border', theme.border)
    root.style.setProperty('--color-tenant-card-selected', theme.cardSelected)
    root.style.setProperty('--color-tenant-bg-1', theme.bgGrad1)
    root.style.setProperty('--color-tenant-bg-2', theme.bgGrad2)
    // RGB triplets for use in `rgba(var(--x), opacity)` (radial gradients)
    root.style.setProperty('--color-tenant-bg-1-rgb', hexToRgbTriplet(theme.bgGrad1))
    root.style.setProperty('--color-tenant-bg-2-rgb', hexToRgbTriplet(theme.bgGrad2))
    return () => {
      root.style.setProperty('--color-tenant-primary', DEFAULT_PRIMARY)
      root.style.setProperty('--color-tenant-hover', DEFAULT_PRIMARY_HOVER)
      root.style.setProperty('--color-tenant-accent', DEFAULT_ACCENT)
      root.style.setProperty('--color-tenant-on', DEFAULT_ON_PRIMARY)
      root.style.setProperty('--color-tenant-surface', DEFAULT_SURFACE)
      root.style.setProperty('--color-tenant-elevated', DEFAULT_ELEVATED)
      root.style.setProperty('--color-tenant-border', DEFAULT_BORDER)
      root.style.setProperty('--color-tenant-card-selected', DEFAULT_CARD_SELECTED)
      root.style.setProperty('--color-tenant-bg-1', DEFAULT_BG_GRAD_1)
      root.style.setProperty('--color-tenant-bg-2', DEFAULT_BG_GRAD_2)
      root.style.setProperty('--color-tenant-bg-1-rgb', hexToRgbTriplet(DEFAULT_BG_GRAD_1))
      root.style.setProperty('--color-tenant-bg-2-rgb', hexToRgbTriplet(DEFAULT_BG_GRAD_2))
    }
  }, [theme])

  return (
    <PortalThemeContext.Provider value={theme}>{children}</PortalThemeContext.Provider>
  )
}

export function usePortalTheme(): PortalTheme {
  const ctx = useContext(PortalThemeContext)
  if (!ctx) {
    // Works even without a provider — returns defaults (useful for previews)
    return {
      primary: DEFAULT_PRIMARY,
      primaryHover: DEFAULT_PRIMARY_HOVER,
      accent: DEFAULT_ACCENT,
      textOnPrimary: DEFAULT_ON_PRIMARY,
      surface: DEFAULT_SURFACE,
      elevated: DEFAULT_ELEVATED,
      border: DEFAULT_BORDER,
      cardSelected: DEFAULT_CARD_SELECTED,
      bgGrad1: DEFAULT_BG_GRAD_1,
      bgGrad2: DEFAULT_BG_GRAD_2,
      source: 'default',
    }
  }
  return ctx
}
